import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { ACADEMIC_YEAR_OPTIONS } from "../utils/constants";
import {
  selectCrmLeadsFilters,
  setCrmLeadsAcademicYear,
} from "../../../redux/slices/filterSlice";

interface AcademicYearFilterProps {
  onClose: () => void;
}

const AcademicYearFilter = ({ onClose }: AcademicYearFilterProps) => {
  const dispatch = useDispatch();
  const academicYear = useSelector(selectCrmLeadsFilters)?.academicYear;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYears, setSelectedYears] = useState<string[]>(
    Array.isArray(academicYear?.value) ? [...academicYear.value] : []
  );

  const filteredYears = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return ACADEMIC_YEAR_OPTIONS;
    return ACADEMIC_YEAR_OPTIONS.filter((option) =>
      option.content.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const unselectedYears = useMemo(
    () => filteredYears.filter((option) => !selectedYears.includes(option.value)),
    [filteredYears, selectedYears]
  );

  const handleToggleYear = (year: string) => {
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const handleApply = () => {
    const displayValues = selectedYears.map(
      (year) =>
        ACADEMIC_YEAR_OPTIONS.find((option) => option.value === year)
          ?.content || year
    );

    dispatch(
      setCrmLeadsAcademicYear({
        value: selectedYears,
        displayValue: displayValues,
      })
    );
    onClose();
  };

  const isApplyDisabled =
    JSON.stringify([...selectedYears].sort()) ===
    JSON.stringify([...(academicYear?.value || [])].sort());

  return (
    <div className="flex flex-col h-[92vh] overflow-hidden">
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search Academic Years"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {selectedYears.length > 0 && (
        <div className="shrink-0">
          <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1 block">
            SELECTED ({selectedYears?.length ?? 0})
          </span>
          <div className="max-h-[200px] overflow-y-auto">
            <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
              {selectedYears.map((yearValue) => (
                <div
                  key={yearValue}
                  onClick={() => handleToggleYear(yearValue)}
                  className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                      {ACADEMIC_YEAR_OPTIONS.find(
                        (option) => option.value === yearValue
                      )?.content || yearValue}
                    </span>
                  </div>
                  <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                    <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Academic Years
        </div>

        <div className="flex-1 overflow-y-auto">
          {unselectedYears.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim()
                  ? "No academic years found"
                  : "No academic years available"}
              </div>
            </div>
          ) : (
            unselectedYears.map((option) => (
              <div
                key={option.value}
                onClick={() => handleToggleYear(option.value)}
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                    {option.content}
                  </div>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                  <div className="w-3 h-3 rounded-sm bg-transparent" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default AcademicYearFilter;
